/**
 * 動画の画質選択リスト
 *
 * プレイヤーのオーバーレイとダウンロードのシートで共用する。
 * 未生成の画質はサイズが予測値のため「約」を付けて表示する。
 */
import { Check, Loader2 } from 'lucide-react'

import type { VideoQualityOption } from '@/api/api'
import { formatBytes } from '@/lib/format-utils'
import { cn } from '@/lib/utils'

type Props = {
  options: VideoQualityOption[]
  selectedQuality: string
  onSelect: (quality: string) => void
  /** 変換中の画質と進捗 */
  conversion?: { quality: string; progress: number } | null
  /** 暗い背景（プレイヤーオーバーレイ）向けの配色にする */
  dark?: boolean
  disabled?: boolean
}

const formatSize = (option: VideoQualityOption): string => {
  if (option.file_size === null) return '-'
  const size = formatBytes(option.file_size)
  return option.is_estimated ? `約 ${size}` : size
}

export const QualityOptionList = ({
  options,
  selectedQuality,
  onSelect,
  conversion = null,
  dark = false,
  disabled = false,
}: Props) => {
  return (
    <div className="flex flex-col gap-1">
      {options.map((option) => {
        const isSelected = option.quality === selectedQuality
        const isConverting = conversion?.quality === option.quality
        const needsConversion = option.quality !== 'original' && option.status !== 'ready'

        return (
          <button
            key={option.quality}
            type="button"
            disabled={disabled || isConverting}
            onClick={() => onSelect(option.quality)}
            className={cn(
              'flex w-full items-center justify-between rounded-md px-3 py-2! text-left transition-colors',
              dark ? 'text-white hover:bg-white/10' : 'text-foreground hover:bg-accent',
              isSelected && (dark ? 'bg-white/15' : 'bg-accent'),
              (disabled || isConverting) && 'opacity-60',
            )}
          >
            <span className="flex min-w-0 flex-col">
              <span className="flex items-center gap-2 text-sm font-medium">
                {option.label}
                {needsConversion && (
                  <span className={cn('text-[10px]', dark ? 'text-white/50' : 'text-muted-foreground')}>未生成</span>
                )}
              </span>
              <span className={cn('text-xs', dark ? 'text-white/60' : 'text-muted-foreground')}>
                {option.width}×{option.height}
              </span>
            </span>

            <span className="flex shrink-0 items-center gap-2">
              {isConverting ? (
                <span className="flex items-center gap-1.5 text-xs">
                  <Loader2 className="size-3.5 animate-spin" />
                  {conversion.progress}%
                </span>
              ) : (
                <span className={cn('text-xs tabular-nums', dark ? 'text-white/70' : 'text-muted-foreground')}>
                  {formatSize(option)}
                </span>
              )}
              {isSelected && !isConverting && <Check className="size-4" />}
            </span>
          </button>
        )
      })}
    </div>
  )
}
